import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  HelpCircle, Database, GitBranch, Search, ShieldCheck,
  Zap, AlertTriangle, FolderTree, HardDrive, Upload, BookOpen,
  Layers, Eye, ArrowRight, CheckCircle2, Clock
} from 'lucide-react';

function Section({ icon: Icon, title, children, color = 'text-zinc-600' }) {
  return (
    <div className="space-y-2">
      <h3 className={`text-xs font-semibold flex items-center gap-1.5 ${color}`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </h3>
      <div className="text-[11px] text-zinc-600 leading-relaxed pl-5 space-y-1.5">
        {children}
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-2.5">
      <div className="shrink-0 w-5 h-5 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
        {n}
      </div>
      <div className="text-[11px] text-zinc-600 space-y-1">
        <p className="font-semibold text-zinc-800">{title}</p>
        <div className="space-y-1">{children}</div>
      </div>
    </div>
  );
}

export default function KBHelpPanel() {
  return (
    <div className="space-y-4" data-testid="kb-help-panel">
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
        <HelpCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-blue-700">Base de Conocimiento — guía operativa</p>
          <p className="text-[11px] text-blue-600 mt-1">
            La KB no es un archivo suelto: es un sistema de <b>orígenes → indexación → recuperación con scope</b>,
            más una capa de <b>memoria organizacional</b> (libro de historia) que capitaliza resoluciones y
            parámetros ERP. Esta ayuda describe qué hace cada pestaña y cuándo aparece (o no) la información.
          </p>
        </div>
      </div>

      {/* ── Flujo general ── */}
      <Card className="border-zinc-200 rounded-sm">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" /> Cómo fluye el conocimiento
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono text-zinc-700 py-1 mb-3">
            <span className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-sm">Orígenes</span>
            <ArrowRight className="w-3 h-3 text-zinc-400" />
            <span className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-sm">Chunks + embeddings</span>
            <ArrowRight className="w-3 h-3 text-zinc-400" />
            <span className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-sm">Qdrant (code/docs/…)</span>
            <ArrowRight className="w-3 h-3 text-zinc-400" />
            <span className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-sm">Agente (scope cápsula)</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-[11px] text-zinc-600">
            <div className="space-y-2">
              <p className="font-semibold text-zinc-800">1. Orígenes (qué se indexa)</p>
              <ul className="list-disc ml-4 space-y-1">
                <li><b>Mounts host</b> bajo <span className="font-mono">/data/sources</span> (RO) y <b>mounts administrables</b> con upload UI.</li>
                <li><b>Repos Git</b> del pool del tenant.</li>
                <li><b>Archivos manuales</b> por cápsula (Config → Conocimiento).</li>
                <li><b>Artefactos de ticket</b> y adjuntos de canal.</li>
                <li><b>Paramétrico / Playbooks / Sucesos aprobados</b> (memoria organizacional) → colección <span className="font-mono">docs</span>.</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-zinc-800">2. Recuperación (qué ve el agente)</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>Búsqueda semántica en Qdrant filtrada por <span className="font-mono">mount_id</span> / <span className="font-mono">repo_id</span> / <span className="font-mono">capsule_id</span>.</li>
                <li>Herramientas agenticas: <span className="font-mono">kb_search</span>, <span className="font-mono">kb_grep</span>, <span className="font-mono">kb_glob</span>, lectura de fuente.</li>
                <li>Políticas en pestaña <b>Configuración</b> (min_score, max_results, colecciones).</li>
                <li>Probar con <b>Playground</b> eligiendo cápsula + agente.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
                Tipos de origen
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-4">
              <Section icon={FolderTree} title="Mounts locales (host y administrables)" color="text-blue-600">
                <p><b>Host</b>: subcarpetas bajo <span className="font-mono">/data/sources</span> (solo lectura; las carga ops en el servidor).</p>
                <p><b>Administrable</b>: volumen RW (<span className="font-mono">/data/kb-managed</span>). Desde la UI podés subir carpeta o ZIP (fusionar/reemplazar) e indexar. Ideal para corpus que no viven en Git.</p>
                <p>Varios mounts se comparten entre cápsulas vía suscripción. Solo se indexa lo declarado.</p>
              </Section>
              <Section icon={GitBranch} title="Repositorios Git (pool tenant)" color="text-indigo-600">
                <p>Repos clonados en <span className="font-mono">/data/repos</span>. Sync manual, por intervalo o webhook.</p>
              </Section>
              <Section icon={Upload} title="Archivos manuales por cápsula" color="text-green-600">
                <p>Subida en Config Cápsula → Conocimiento. Van a <span className="font-mono">/data/capsule-files</span> con <span className="font-mono">capsule_id</span>.</p>
              </Section>
              <Section icon={HardDrive} title="Tickets y adjuntos" color="text-amber-600">
                <p>Artefactos del workspace del ticket y adjuntos de canal se indexan con scope de cápsula.</p>
              </Section>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
                Pestañas de esta pantalla
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2 text-[11px] text-zinc-600">
              <p><b>Playground:</b> prueba RAG + respuesta LLM con el scope efectivo de cápsula/agente.</p>
              <p><b>Grafo:</b> mapa de <b>código y DDL</b> (packages, tablas, FKs, calls). No incluye el árbol semántico Apex (`.md` de páginas/regiones); ese se explora con tools agenticas / RAG en Playground o chat. Estadísticas omiten nodos fuera del subset visual.</p>
              <p><b>Contenido:</b> navegar archivos indexados con RBAC.</p>
              <p><b>Orígenes:</b> volumen, mounts, repos (admin).</p>
              <p><b>Configuración:</b> políticas de retrieval por tenant/cápsula/agente.</p>
              <p><b>Memoria:</b> observatorio del libro de historia + paramétrico ERP + playbooks (ver sección abajo).</p>
              <p><b>Ayuda:</b> esta guía.</p>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
                Qué esperar de los agentes
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-4">
              <Section icon={Search} title="Retrieval con scope" color="text-green-600">
                <p>La búsqueda filtra por orígenes autorizados. Mounts compartidos son visibles a todas las cápsulas suscritas.</p>
              </Section>
              <Section icon={Zap} title="Harness agentico (investigation)" color="text-purple-600">
                <p>Con Runtime → investigation <span className="font-mono">iterative</span>, el agente hace un bucle interno:
                  <span className="font-mono"> kb_list_dir</span> / <span className="font-mono">kb_glob</span> /
                  <span className="font-mono"> kb_grep</span> / <span className="font-mono">kb_read_source</span> /
                  <span className="font-mono"> kb_search</span> / <span className="font-mono">kb_expand_graph</span>.
                  PDF/DOCX/XLSX vía indexación. Playbook: <span className="font-mono">soporte-kb-investigation-RULES-playbook.md</span>.</p>
              </Section>
              <Section icon={ShieldCheck} title="Gobernanza" color="text-red-600">
                <p>Operadores solo ven mounts/repos de sus cápsulas. Admin ve todo el tenant.</p>
              </Section>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          {/* ── MEMORIA DETALLADA ── */}
          <Card className="border-violet-200 rounded-sm bg-violet-50/30">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-violet-700 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Memoria organizacional (libro de historia)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-4 text-[11px] text-zinc-700">
              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-sm text-amber-900">
                <p className="font-semibold flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> No es por tiempo de espera
                </p>
                <p className="mt-1">
                  Crear un ticket (mail a Del.ia, WhatsApp, webchat) <b>no genera un suceso</b>.
                  El borrador de memoria aparece cuando el ticket pasa a <b>resolved</b> o <b>closed</b>
                  (extracción automática), o cuando alguien pulsa <b>Extraer borrador</b> en
                  Tickets → pestaña Memoria. Por eso en Sucesos seguís viendo solo tickets ya tratados.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-900 mb-2">Ciclo de vida de un suceso</p>
                <div className="space-y-3">
                  <Step n={1} title="Ticket abierto / en curso">
                    <p>El caso vive en Tickets. La KB clásica (docs, código, paramétrico) ya puede usarse;
                      el <b>libro de historia</b> aún no tiene un suceso de este ticket.</p>
                  </Step>
                  <Step n={2} title="Resolver o cerrar → borrador (draft)">
                    <p>El backend extrae título, síntomas, causa, pasos y tags de óptica (fail-open: si falla, el ticket igual se cierra).
                      Estado <Badge variant="outline" className="text-[9px] rounded-sm mx-0.5">draft</Badge>.
                      Visible en observatorio Sucesos y en Tickets → Memoria.</p>
                  </Step>
                  <Step n={3} title="Revisar y promover (humano)">
                    <p>En Tickets → Memoria: editar, opcionalmente «compartir tenant», confirmar con diálogo.
                      Pasa a <Badge variant="outline" className="text-[9px] rounded-sm mx-0.5">approved</Badge> e indexa
                      markdown en Qdrant (<span className="font-mono">MemoryEvents/…</span>).</p>
                  </Step>
                  <Step n={4} title="Capítulos e índices">
                    <p>El curador ubica el suceso en un <b>capítulo</b> temático y alimenta <b>índices por óptica</b>
                      (causal, táctica, cronológica, proceso). En el observatorio: Capítulos / Índices / Curador.</p>
                  </Step>
                  <Step n={5} title="Uso en runtime">
                    <p>Con flag de inyección activo, el agente recibe casos similares aprobados en el system prompt
                      al atender consultas parecidas. Verificá con Búsqueda del observatorio o Playground.</p>
                  </Step>
                </div>
              </div>

              <div>
                <p className="font-semibold text-zinc-900 mb-1">Cómo actualizar / forzar memoria de un ticket</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Abrí el ticket en <b>Tickets</b>.</li>
                  <li>Pestaña <b>Memoria</b> → <b>Extraer borrador</b> (aunque aún esté abierto) o esperá a resolver/cerrar.</li>
                  <li>Editá síntomas / causa / pasos → <b>Guardar</b> → <b>Promover</b>.</li>
                  <li>En Base de Conocimiento → <b>Memoria</b> → <b>Actualizar</b>: debería aparecer en Sucesos.</li>
                  <li>Opcional: Curador → Ejecutar → aprobar propuestas de consolidación.</li>
                </ol>
              </div>

              <div>
                <p className="font-semibold text-zinc-900 mb-1">¿Dónde va esto? (regla humana, no automática)</p>
                <div className="border border-violet-100 rounded-sm overflow-hidden">
                  <table className="w-full text-[10px]">
                    <thead className="bg-violet-50/80">
                      <tr className="text-left text-violet-800">
                        <th className="p-1.5 font-medium">Si tenés…</th>
                        <th className="p-1.5 font-medium">Va a…</th>
                        <th className="p-1.5 font-medium">Cómo entra</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-700">
                      <tr className="border-t border-violet-50">
                        <td className="p-1.5">Lección de un ticket resuelto</td>
                        <td className="p-1.5 font-medium">Suceso (libro)</td>
                        <td className="p-1.5">Extraer → Promover</td>
                      </tr>
                      <tr className="border-t border-violet-50">
                        <td className="p-1.5">Tabla maestra ERP (almacenes, IVA…)</td>
                        <td className="p-1.5 font-medium">Paramétrico</td>
                        <td className="p-1.5">Export/API admin (no el mail)</td>
                      </tr>
                      <tr className="border-t border-violet-50">
                        <td className="p-1.5">Procedimiento escrito del equipo</td>
                        <td className="p-1.5 font-medium">Playbook</td>
                        <td className="p-1.5">Carga SME / admin</td>
                      </tr>
                      <tr className="border-t border-violet-50">
                        <td className="p-1.5">Stock/saldo del momento</td>
                        <td className="p-1.5 font-medium">Live habilidad</td>
                        <td className="p-1.5">API solo lectura (no RAG fijo)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">
                  El botón «Zona demo: bootstrap» solo siembra ejemplos de prueba (badge bootstrap-demo). Borrarlos con Eliminar.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-900 mb-1">Cómo curar / borrar (evitar confusión al agente)</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li><b>Suceso malo o confuso:</b> observatorio → Sucesos → Archivar (deja de inyectarse) o Eliminar (hard).</li>
                  <li><b>Borrador basura:</b> Tickets → Memoria → Descartar borrador.</li>
                  <li><b>Snapshot / playbook demo:</b> pestaña Paramétrico o Playbooks → Eliminar (Mongo + Qdrant).</li>
                  <li>Confirmación siempre con diálogo in-app (sin window.confirm).</li>
                </ol>
              </div>

              <div>
                <p className="font-semibold text-zinc-900 mb-1">Capas que conviven (no son lo mismo)</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li><b>Suceso / capítulo / índice:</b> aprendizaje de resoluciones (historia).</li>
                  <li><b>Paramétrico:</b> snapshots estables ERP (<span className="font-mono">Parametric/almacenes.md</span>, etc.) — maestro, no saldos live.</li>
                  <li><b>Playbooks:</b> procedimientos SME (<span className="font-mono">Playbooks/…</span>).</li>
                  <li><b>Live habilidades:</b> datos volátiles vía API solo lectura (cuando estén habilitadas); no viven solo en el vector store.</li>
                  <li><b>Código / docs de mounts:</b> KB clásica; no sustituyen el paramétrico operativo.</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-zinc-900 mb-1">Qué hace cada subpestaña del observatorio</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li><b>Mapa:</b> diagrama de capas, ópticas, pilares del <span className="font-mono">knowledge_profile</span>.</li>
                  <li><b>Libro de historia:</b> Sucesos | Capítulos | Índices | Curador | Búsqueda.</li>
                  <li><b>Fuentes ERP/SME:</b> Paramétrico | Playbooks | Perfil (no son etapas del ticket).</li>
                </ul>
                <p className="text-zinc-500 mt-2">
                  Deep link: <span className="font-mono">/knowledge?tab=memory&amp;capsule=sistemas</span>
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5 pt-1">
                <Badge variant="outline" className="text-[9px] rounded-sm font-mono">ORG_MEMORY_ENABLED</Badge>
                <Badge variant="outline" className="text-[9px] rounded-sm font-mono">AUTO_EXTRACT_ON_RESOLVE</Badge>
                <Badge variant="outline" className="text-[9px] rounded-sm font-mono">RUNTIME_INJECT</Badge>
                <Badge variant="outline" className="text-[9px] rounded-sm font-mono">CURATOR</Badge>
                <Badge variant="outline" className="text-[9px] rounded-sm font-mono">PARAMETRIC_RAG</Badge>
              </div>
              <p className="text-[10px] text-zinc-500">
                Los flags se ven en el encabezado del observatorio Memoria. Si estánEXTRACT está off, no habrá borrador automático al cerrar.
              </p>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Cómo verificar que «está en la KB»
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2 text-[11px] text-zinc-600">
              <p><CheckCircle2 className="w-3 h-3 inline text-emerald-600 mr-1" />
                Paramétrico: badge <b>Qdrant: N chunks</b> + vista previa de filas/markdown.</p>
              <p><CheckCircle2 className="w-3 h-3 inline text-emerald-600 mr-1" />
                Suceso aprobado: aparece en Sucesos con estado approved; buscá el título en Búsqueda o Playground.</p>
              <p><CheckCircle2 className="w-3 h-3 inline text-emerald-600 mr-1" />
                Playbooks: contenido en pestaña + chunks Qdrant.</p>
              <p className="text-zinc-500">
                Los paths <span className="font-mono">Parametric/</span> y <span className="font-mono">Playbooks/</span> se indexan directo a Qdrant;
                no siempre aparecen como archivos de mount en Contenido.
              </p>
            </CardContent>
          </Card>

          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700">
              Si ves miles de archivos indexados sin haber configurado mounts, son datos legacy del scan antiguo.
              Ejecutá <b>Purga legacy + reindex</b> en Orígenes tras declarar tus mounts.
              Los snapshots demo del bootstrap piloto <b>no sustituyen</b> exports ERP reales.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
